<template>
  <div class="control-island">
    <div class="control-content">
      <!-- Status Dropdown -->
      <div class="filter-group">
        <label>Status</label>
        <select v-model="selectedStatus" @change="emitFilters" class="filter-select">
          <option value="all">All</option>
          <option value="abandoned">Abandoned</option>
          <option value="derelict">Derelict</option>
          <option value="vacant">Vacant</option>
          <option value="ruin">Ruins</option>
        </select>
      </div>

      <!-- Before Date Filter -->
      <div class="filter-group">
        <label>Before Date</label>
        <input 
          type="date" 
          v-model="beforeDate" 
          @change="emitFilters"
          class="date-input"
        />
      </div>

      <!-- Region Selector -->
      <div class="filter-group">
        <label>Region</label>
        <select v-model="selectedRegion" @change="onRegionChange" class="filter-select">
          <option value="all">All Australia</option>
          <option value="nsw">New South Wales</option>
          <option value="vic">Victoria</option>
          <option value="qld">Queensland</option>
          <option value="wa">Western Australia</option>
          <option value="sa">South Australia</option>
          <option value="tas">Tasmania</option>
          <option value="act">ACT</option>
          <option value="nt">Northern Territory</option>
        </select>
      </div>

      <!-- Hide Unnamed Checkbox -->
      <div class="filter-group checkbox-group">
        <label class="checkbox-label">
          <input 
            type="checkbox" 
            v-model="hideUnnamed" 
            @change="emitFilters"
            class="checkbox-input"
          />
          <span>Hide Unnamed</span>
        </label>
      </div>

      <!-- Stats -->
      <div class="filter-group stats">
        <div class="stat-item">
          <span class="stat-label">Found:</span>
          <span class="stat-value">{{ stats.filtered }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Total:</span>
          <span class="stat-value">{{ stats.total }}</span>
        </div>
      </div>

      <!-- Debug Mode Toggle -->
      <div class="filter-group debug-toggle" v-if="debugMode">
        <button @click="$emit('toggle-upload')" class="debug-btn" title="Debug: Manual Upload">
          Upload
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ControlIsland',
  props: {
    stats: {
      type: Object,
      default: () => ({ total: 0, filtered: 0 })
    },
    debugMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedStatus: 'all',
      beforeDate: '',
      selectedRegion: 'all',
      hideUnnamed: false
    };
  },
  methods: {
    onRegionChange() {
      this.emitFilters();
    },
    emitFilters() {
      this.$emit('filters-changed', {
        status: this.selectedStatus,
        beforeDate: this.beforeDate,
        region: this.selectedRegion,
        hideUnnamed: this.hideUnnamed
      });
    }
  },
  mounted() {
    // Enable debug mode with keyboard shortcut
    window.addEventListener('keydown', (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        this.$emit('toggle-debug');
      }
    });
  }
};
</script>

<style scoped>
.control-island {
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  background: #000000da;
  backdrop-filter: blur(30px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  z-index: 1000;
  max-width: 90vw;
  transition: all 0.3s ease;
}

.control-content {
  display: flex;
  gap: 1.5rem;
  align-items: flex-end;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-group label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.85rem;
  font-weight: 500;
}

.filter-select,
.date-input {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 0.6rem 0.8rem;
  color: white;
  font-size: 0.9rem;
  outline: none;
  transition: all 0.2s ease;
  min-width: 150px;
}

.filter-select option {
  background: #1a1a1f;
  color: white;
}

.filter-select:hover,
.date-input:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

.filter-select:focus,
.date-input:focus {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(66, 153, 225, 0.5);
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
}

.date-input {
  color-scheme: dark;
}

.stats {
  flex-direction: row;
  gap: 1rem;
  padding: 0.6rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.stat-item {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.stat-label {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
}

.stat-value {
  color: rgba(66, 185, 131, 0.9);
  font-weight: 600;
  font-size: 1rem;
}

/* Checkbox Group Styles */
.checkbox-group {
  justify-content: center;
  padding: 0.6rem 0;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  font-weight: 500;
  user-select: none;
}

.checkbox-input {
  appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
}

.checkbox-input:hover {
  border-color: rgba(66, 153, 225, 0.5);
  background: rgba(255, 255, 255, 0.15);
}

.checkbox-input:checked {
  background: rgba(66, 153, 225, 0.4);
  border-color: rgba(66, 153, 225, 0.7);
}

.checkbox-input:checked::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 14px;
  font-weight: bold;
}

.checkbox-label:hover .checkbox-input {
  border-color: rgba(66, 153, 225, 0.5);
}

.debug-toggle {
  margin-left: auto;
}

.debug-toggle {
  margin-left: auto;
}

.debug-btn {
  background: rgba(255, 152, 0, 0.3);
  border: 1px solid rgba(255, 152, 0, 0.5);
  color: white;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.debug-btn:hover {
  background: rgba(255, 152, 0, 0.4);
  border-color: rgba(255, 152, 0, 0.7);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
}

/* Responsive Design */
@media (max-width: 768px) {
  .control-island {
    bottom: 0.5rem;
    left: 0.5rem;
    right: 0.5rem;
    transform: none;
    max-width: none;
    padding: 0.75rem;
    border-radius: 12px;
  }

  .control-content {
    gap: 0.75rem;
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group {
    width: 100%;
    gap: 0.35rem;
  }

  .filter-group label {
    font-size: 0.75rem;
  }

  .filter-select,
  .date-input {
    width: 100%;
    min-width: unset;
    padding: 0.5rem 0.6rem;
    font-size: 0.85rem;
    border-radius: 6px;
  }

  .stats {
    width: 100%;
    justify-content: space-around;
    padding: 0.5rem 0.75rem;
    gap: 0.75rem;
  }

  .stat-label {
    font-size: 0.75rem;
  }

  .stat-value {
    font-size: 0.9rem;
  }

  .debug-toggle {
    margin-left: 0;
    width: 100%;
  }

  .debug-btn {
    width: 100%;
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
  }
}
</style>
